import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  X,
  Bell,
} from "lucide-react";
import { useApi } from "../hooks/useApi";

export const NotificationBell = () => {
  const api = useApi();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const loadUnreadCount = useCallback(async () => {
    try {
      const res = await api.get('/notifications/unread-count');
      setUnreadCount(res.data.unread_count);
    } catch (e) {
      console.error('Failed to load notifications');
    }
  }, [api]);

  useEffect(() => {
    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [loadUnreadCount]);

  const loadNotifications = async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data);
    } catch (e) {
      console.error('Failed to load notifications');
    }
  };

  const handleClick = () => {
    setShowDropdown(!showDropdown);
    if (!showDropdown) {
      loadNotifications();
    }
  };

  const markAsRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      loadUnreadCount();
      loadNotifications();
    } catch (e) {
      console.error('Failed to mark as read');
    }
  };

  const markAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setUnreadCount(0);
      loadNotifications();
    } catch (e) {
      console.error('Failed to mark all as read');
    }
  };

  return (
    <div className="relative">
      <button 
        onClick={handleClick}
        className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition relative"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-gray-300" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {showDropdown && createPortal(
        <>
          <div 
            className="fixed inset-0 bg-black/50" 
            style={{ zIndex: 9998 }}
            onClick={() => setShowDropdown(false)} 
          />
          <div 
            className="fixed w-[90vw] max-w-md bg-slate-900 border border-purple-500/30 rounded-2xl shadow-2xl overflow-hidden"
            style={{ 
              zIndex: 9999,
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)'
            }}
          >
            <div className="p-4 border-b border-purple-500/20 flex items-center justify-between bg-slate-800">
              <h3 className="font-semibold text-white text-lg">Notifications</h3>
              <div className="flex items-center gap-3">
                {unreadCount > 0 && (
                  <button onClick={markAllRead} className="text-xs text-cyan-400 hover:underline">
                    Mark all read
                  </button>
                )}
                <button onClick={() => setShowDropdown(false)} className="text-gray-400 hover:text-white p-1 hover:bg-white/10 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-10 text-center text-gray-500">
                  <Bell className="w-14 h-14 mx-auto mb-4 opacity-50" />
                  <p className="text-base">No notifications yet</p>
                  <p className="text-sm mt-2">You'll see updates here when something happens</p>
                </div>
              ) : (
                notifications.slice(0, 10).map((notif) => (
                  <div
                    key={notif.id}
                    className={`p-4 border-b border-purple-500/10 hover:bg-white/5 cursor-pointer transition ${!notif.read ? 'bg-purple-500/10' : ''}`}
                    onClick={() => {
                      markAsRead(notif.id);
                      if (notif.action_url) navigate(notif.action_url);
                      setShowDropdown(false);
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-3 h-3 rounded-full mt-1.5 flex-shrink-0 ${!notif.read ? 'bg-cyan-400' : 'bg-gray-600'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-white">{notif.title}</p>
                        <p className="text-gray-400 text-sm mt-1 line-clamp-2">{notif.message}</p>
                        <p className="text-gray-500 text-xs mt-2">
                          {new Date(notif.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

// Dashboard Layout
